import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { getFilterOptions, getFooterLinks } from '../../../new-lib/apis';
import styled from 'styled-components';
import { GetStaticPaths, GetStaticPropsContext } from 'next';
import { useContext, useEffect, useRef, useState } from 'react';
import { getNowDate } from '../../../helpers/search-options/common';
import { wrapper } from '../../../redux/store';
import {
  setExperimentVariant,
  setIsMobile
} from '../../../redux/actions/appActions';
import { useSelector } from 'react-redux';
import ImageWrapper from '../../../new-components/ImageWrapper';
import { SearchFiltersActionTypes } from '../../../reducers/search-filters/actions';
import { SearchFiltersTypes } from '../../../reducers/search-filters/reducer';
import ExplorePropertiesContainer from '../../../features/entity/components/explore-properties/ExplorePropertiesContainer';
import NawyNowForm from '../../../features/contact/components/nawy-now/NawyNowForm';
import Modal from '../../../new-components/mobile/Modal';
import { logEvent } from '../../../new-lib/analytics';
import { getPageLocation } from '../../../helpers/common';
import {
  SearchContext,
  SearchContextInterface
} from '../../../features/search-new/context/SearchContext';
import { StaticPagesUtility } from '../../../new-lib/utilities/static-pages.utility';
import { ConfigsApis } from '../../../new-lib/apis/configs.apis';
import { AreasApis } from '../../../new-lib/apis/areas.apis';
import { PropertyTypesApis } from '../../../new-lib/apis/property-types.apis';

interface WrapperProps {
  locale?: string;
}

const NawyNow = (props: any) => {
  const { t } = useTranslation('nawy-now');
  const router = useRouter();
  const { locale, asPath } = router;
  const [openModal, setOpenModal] = useState(false);
  const { searchFiltersDispatch } = useContext(
    SearchContext
  ) as SearchContextInterface;
  const [isButtonVisible, setIsButtonVisible] = useState(true);
  const formRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useSelector((state: any) => state.app.isMobile);

  useEffect(() => {
    searchFiltersDispatch({
      type: SearchFiltersActionTypes.SET_INITIAL_FILTERS,
      payload: {
        initial_filters: [
          {
            filter: SearchFiltersTypes.NAWY_NOW,
            value: true
          },
          {
            filter: SearchFiltersTypes.DELIVERY_DATE,
            value: getNowDate()
          },
          {
            filter: SearchFiltersTypes.CATEGORY,
            value: 'property'
          }
        ]
      }
    });
    return () => {
      searchFiltersDispatch({
        type: SearchFiltersActionTypes.RESET
      });
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (formRef.current) {
        const formTop = formRef.current.getBoundingClientRect().top;
        const formBottom = formRef.current.getBoundingClientRect().bottom;

        if (formTop < window.innerHeight && formBottom >= 0) {
          setIsButtonVisible(false);
        } else {
          setIsButtonVisible(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleButtonClick = () => {
    logEvent.click('form_btn_click', getPageLocation(asPath), 'nawy_now');
    if (formRef.current) {
      const offset = 100;
      const targetPosition =
        formRef?.current.getBoundingClientRect().top - offset;

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  const renderStep = (
    imageSrc: string,
    titleKey: string,
    descriptionKey: string
  ) => (
    <div className="step">
      <ImageWrapper
        src={`/assets/icons/nawy-now/${imageSrc}`}
        alt={imageSrc}
        width={240}
        height={100}
      />
      <span>{t(titleKey)}</span>
      <p>{t(descriptionKey)}</p>
    </div>
  );

  const renderMobileStep = (
    imageSrc: string,
    titleKey: string,
    descriptionKey: string
  ) => (
    <div className="step">
      <div className="row-container">
        <ImageWrapper
          src={`/assets/icons/nawy-now/${imageSrc}`}
          alt={imageSrc}
          width={50}
          height={45}
        />
        <span>{t(titleKey)}</span>
      </div>
      <p>{t(descriptionKey)}</p>
    </div>
  );

  return (
    <Wrapper locale={locale}>
      <div className="nawy-now-introduction">
        <div className={`nawy-now-header container-fluid`}>
          <div className="header-title">
            <h1>{t('nawy_now_header')}</h1>
            {!isMobile && (
              <button className="nawy-form-btn" onClick={handleButtonClick}>
                {t('form_btn')}
              </button>
            )}
          </div>
          <p>{t('move_now_description')}</p>
        </div>
        {!isMobile ? (
          <div className="nawy-now-steps container-fluid">
            {renderStep(
              'move_now.svg',
              'step_one_title',
              'step_one_description'
            )}
            {renderStep(
              'payment-plan.svg',
              'step_two_title',
              'step_two_description'
            )}
            {renderStep(
              'delivery.svg',
              'step_three_title',
              'step_three_description'
            )}
          </div>
        ) : (
          <div className="container-fluid">
            <div className="nawy-now-steps">
              {renderMobileStep(
                'move_now.svg',
                'step_one_title',
                'step_one_description'
              )}
              {renderMobileStep(
                'payment-plan.svg',
                'step_two_title',
                'step_two_description'
              )}
              {renderMobileStep(
                'delivery.svg',
                'step_three_title',
                'step_three_description'
              )}
            </div>
          </div>
        )}
      </div>
      <div className="nawy-now-properties-container container-fluid">
        <ExplorePropertiesContainer
          exploreLocation="nawy-now-page"
          filterOptions={props.filterOptions}
          headline="explore_nawy_now_properties"
        />
      </div>
      <div className="container-fluid" ref={formRef}>
        <NawyNowForm
          isModal={false}
          setModalOpen={setOpenModal}
          areas={props.areas?.map((area: any) => ({
            value: area.value,
            label: area.options?.displayedValue
          }))}
          propertyTypes={props.propertyTypes?.map((propertyType: any) => ({
            value: propertyType.id,
            label: propertyType.name
          }))}
        />
      </div>
      {isMobile && isButtonVisible && (
        <button
          className="action-btn"
          onClick={() => {
            setOpenModal(true);
            logEvent.click(
              'form_btn_click',
              getPageLocation(asPath),
              'nawy_now'
            );
          }}
        >
          {t('form_btn')}
        </button>
      )}
      <Modal
        isOpen={openModal}
        withCloseBtn={true}
        closeFn={() => setOpenModal(false)}
      >
        <NawyNowForm
          isModal={true}
          setModalOpen={setOpenModal}
          areas={props.areas?.map((area: any) => ({
            value: area.id,
            label: area.name
          }))}
          propertyTypes={props.propertyTypes?.map((propertyType: any) => ({
            value: propertyType.id,
            label: propertyType.name
          }))}
        />
      </Modal>
    </Wrapper>
  );
};
export default NawyNow;

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;

  .modal-content {
    padding: 0px 0px !important;
  }

  .nawy-now-introduction {
    display: flex;
    flex-direction: column;
    background: #f0f1f3;
    justify-content: center;
    align-items: flex-start;
    gap: 32px;
    padding: 64px 32px 40px 32px;
  }
  .nawy-now-header {
    .header-title {
      display: flex;
      justify-content: space-between;
    }
    h1 {
      color: #015c9a;
      font-family: 'materialBold';
      font-size: 40px;
      line-height: normal;
      text-transform: capitalize;
    }
    button {
      color: #015c9a;
      text-align: center;
      font-family: 'materialRegular';
      font-size: 16px;
      line-height: normal;
      text-decoration-line: underline;
      text-transform: capitalize;
      background: transparent;
      border-color: transparent;
    }

    p {
      color: #2c3138;
      font-family: 'materialMedium';
      font-size: 24px;
      line-height: normal;
      text-transform: capitalize;
    }
  }
  .nawy-now-steps {
    display: flex;
    margin-top: 80px;
    gap: 16px;
    .step {
      position: relative;
      display: flex;
      padding: 54px 16px 24px 16px;
      flex-direction: column;
      align-items: center;
      gap: 24px;
      border-radius: 12px;
      background: #fff;
      width: 40%;
      img {
        position: absolute;
        top: -40%;
      }
      span {
        color: #2c3138;
        text-align: center;
        font-family: materialSemiBold;
        font-size: 20px;
        text-transform: capitalize;
      }
      p {
        color: #2c3138;
        text-align: center;
        font-family: materialRegular;
        font-size: 15px;
      }
    }
  }
  .explore-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 64px;
    h2 {
      color: ${props => props.theme.light.colors.black};
      font-size: ${props => props.theme.font.sizes.xxxl};
      font-family: materialBold;
    }
    p {
      margin-top: 5px;
      color: ${props => props.theme.light.colors.black};
      font-size: ${props => props.theme.font.sizes.l};
      font-family: materialLight;
    }
    .searchbtn {
      margin-top: 8px;
    }
  }
  .nawy-now-properties-container {
    margin-top: 32px;
    display: flex;
    flex-direction: column;
    gap: 5px;
    h3 {
      color: #00324c;
      font-size: ${props => props.theme.font.sizes.xl};
      font-family: materialBold;
    }
    .cards-container {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      & > * {
        flex-basis: 24%;
      }
    }
  }

  .action-btn {
    width: 80vw;
    height: 50px;
    display: flex;
    position: fixed;
    bottom: 3%;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1000;
    padding: 8px 16px;
    margin: 0 auto;
    justify-content: center;
    align-items: center;
    gap: 5px;
    color: #fff;
    font-family: 'materialRegular';
    font-size: 14px;
    text-transform: capitalize;
    border-radius: 360px;
    border: transparent;
    background: #ff5e00;
    box-shadow:
      0px 0px 4px 0px rgba(0, 0, 0, 0.04),
      0px 4px 8px 0px rgba(0, 0, 0, 0.06);
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    .nawy-now-introduction {
      display: flex;
      padding: 24px 0px;
      flex-direction: column;
      align-items: flex-start;
      overflow-x: hidden;
      gap: 15px;
    }
    .nawy-now-header {
      margin-top: 0px;
      display: flex;
      padding: 0px 16px;
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;

      h1 {
        color: #015c9a;
        font-family: materialBold;
        font-size: 28px;
        text-transform: capitalize;
        text-align: left;
      }
      p {
        color: #2c3138;
        font-family: materialRegular;
        font-size: 13px;
        text-align: left;
      }
    }
    .nawy-now-steps {
      display: flex;
      flex-direction: row;
      overflow-x: auto;
      -ms-overflow-style: none;
      scrollbar-width: none;
      margin-top: 16px;
      gap: 16px;
      .step {
        display: flex;
        min-width: 70%;
        padding: 16px;
        flex-direction: column;
        align-items: flex-start;
        gap: 24px;
        border-radius: 12px;
        background: #fff;
        .row-container {
          display: flex;
          flex-direction: row;
          align-items: center;
        }
        img {
          position: relative;
          top: 0;
        }
        span {
          color: #2c3138;
          text-align: center;
          font-family: materialSemiBold;
          font-size: 14px;
          text-transform: capitalize;
          text-align: ${props => (props.locale === 'en' ? 'left' : 'right')};
          margin: 0px 5px;
        }
        p {
          color: #2c3138;
          text-align: center;
          font-family: materialRegular;
          font-size: 13px;
          text-align: ${props => (props.locale === 'en' ? 'left' : 'right')};
        }
      }
    }
    .explore-section {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-top: 26px;
      h2 {
        color: ${props => props.theme.light.colors.black};
        font-size: ${props => props.theme.font.sizes.xxxl};
        font-family: materialBold;
        text-align: center;
      }
      p {
        margin-top: 5px;
        color: ${props => props.theme.light.colors.black};
        font-size: ${props => props.theme.font.sizes.s};
        font-family: materialLight;
        text-align: center;
      }
      .searchbtn {
        margin-top: 8px;
        font-size: 16px;
        padding: 8.5px 114.5px;
      }
    }
    .nawy-now-properties-slider {
      height: auto;
      .nawy-now-properties-text {
        margin-top: 26px;
        font-size: ${props => props.theme.font.sizes.xl};
        font-family: materialBold;
      }
    }
  }
`;

export const getStaticProps = wrapper.getStaticProps(
  store =>
    async ({ params, locale }: GetStaticPropsContext) => {
      const [
        footerLinks,
        configVariables,
        filterOptions,
        propertyTypes,
        areas
      ] = await Promise.all([
        getFooterLinks(locale, 'nawy-now'),
        ConfigsApis.getConfigs(locale),
        getFilterOptions(locale),
        PropertyTypesApis.listAll(locale),
        AreasApis.listAll(locale)
      ]);

      store.dispatch(setIsMobile(params?.device === 'mobile'));
      store.dispatch(setExperimentVariant(params?.variant as string));

      return {
        revalidate: 60 * 5,
        props: {
          areas,
          propertyTypes,
          ...(await serverSideTranslations(locale as string, [
            'nawy-now',
            'common',
            'search',
            'entity',
            'contact'
          ])),
          filterOptions: JSON.stringify(filterOptions) || '',
          configVariables,
          layout: {
            footerLinks,
            url: `${process.env.NEXT_PUBLIC_HOST}/nawy-now`
          }
        }
      };
    }
);

export const getStaticPaths = (async () => {
  return { paths: StaticPagesUtility.get(), fallback: false };
}) satisfies GetStaticPaths;
